import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector('input[name="website_alt"]') as HTMLInputElement;
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotInput) {
        formData.delete('website_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9ocitu8mbnljin5aba0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (response.ok && parsed?.code === 'InvalidParameter') {
        setFormStatus('error');
        setFormError(parsed?.meta?.message || '提交失败，请重试');
      } else {
        setFormStatus('error');
        setFormError(parsed?.meta?.message || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络异常，请检查网络后重试');
    }
  };

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteInfo.name}`}
        description={`了解${siteInfo.name}的品牌故事、发展历程和核心优势。高清免费在线影视平台，为数千万用户提供流畅观影体验。`}
        keywords="哈哈影院关于我们,哈哈影院品牌介绍,哈哈影院联系方式"
        canonicalUrl="https://hahacinema.com/about"
        ogTitle={`关于我们 - ${siteInfo.name}`}
        ogDescription={`了解${siteInfo.name}的品牌故事、发展历程和核心优势。`}
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: `关于${siteInfo.name}`,
          description: `了解${siteInfo.name}的品牌故事、发展历程和核心优势。`,
          url: 'https://hahacinema.com/about',
          about: {
            '@type': 'Organization',
            name: siteInfo.name,
            url: siteInfo.url,
            description: siteInfo.description,
            foundingDate: siteInfo.foundedYear.toString(),
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              email: siteInfo.email,
              telephone: siteInfo.phone,
              availableLanguage: ['Chinese'],
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: siteInfo.address,
              addressCountry: 'CN',
            },
          },
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <Breadcrumb />

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 text-center">
              关于哈哈影院
            </h1>
            <p className="text-center text-foreground-500 mt-3 max-w-2xl mx-auto">
              致力于为用户提供最优质的免费在线影视观看体验
            </p>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    哈哈影院成立于{siteInfo.foundedYear}年，是专注于提供高清免费在线影视服务的专业平台。我们深知用户对于流畅观影体验的渴望，因此从创立之初就将"不缓冲、不卡顿"作为核心技术目标。
                  </p>
                  <p>
                    经过多年的持续发展，哈哈影院已经构建了覆盖电影、电视剧、动漫、综艺等全品类的海量影视内容库。无论是热门院线大片，还是经典老剧，用户都能在这里找到心仪的内容。
                  </p>
                  <p>
                    我们拥有一支专业的技术团队和内容运营团队，全天候保障平台的稳定运行和内容的及时更新。哈哈影院采用先进的CDN加速技术，在全国范围内部署了数千个加速节点，确保每一位用户都能获得流畅的观影体验。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20large%20screens%20displaying%20movie%20content%2C%20warm%20lighting%2C%20collaborative%20workspace%2C%20team%20working%20on%20digital%20entertainment%20platform%2C%20cinematic%20atmosphere%2C%20comfortable%20and%20inspiring%20environment&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="哈哈影院办公环境"
                  title="哈哈影院 - 专业影视平台团队"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 text-center mb-8">
              平台数据
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200">
                <p className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">
                  {siteInfo.stats.totalMovies.toLocaleString()}+
                </p>
                <p className="text-sm text-foreground-500 mt-2">电影资源</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200">
                <p className="text-3xl md:text-4xl font-bold text-accent-500 font-heading">
                  {siteInfo.stats.totalSeries.toLocaleString()}+
                </p>
                <p className="text-sm text-foreground-500 mt-2">剧集资源</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200">
                <p className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">
                  {(siteInfo.stats.dailyUsers / 10000).toFixed(0)}万+
                </p>
                <p className="text-sm text-foreground-500 mt-2">日活用户</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50 border border-background-200">
                <p className="text-3xl md:text-4xl font-bold text-accent-500 font-heading">
                  {siteInfo.stats.positiveRating}%
                </p>
                <p className="text-sm text-foreground-500 mt-2">好评率</p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 text-center mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                { name: '张先生', text: '哈哈影院真的是我见过最流畅的免费影视网站，看高清电影完全没有缓冲，体验太好了！强烈推荐给身边的朋友。' },
                { name: '李女士', text: '资源更新得特别快，热播剧基本当天就能看到。而且分类也很清晰，找片子非常方便，已经成为我的追剧首选。' },
                { name: '王同学', text: '作为学生党，哈哈影院对我来说简直是宝藏。免费、不卡顿、片源多，完美满足了我所有的观影需求。' },
              ].map((review, i) => (
                <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{review.text}</p>
                  <p className="mt-3 text-xs text-foreground-500 font-medium">— {review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 text-center mb-3">
              联系我们
            </h2>
            <p className="text-center text-sm text-foreground-500 mb-8">
              有任何问题或建议？欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="flex flex-col items-center p-5 rounded-lg bg-background-100 text-center">
                <i className="ri-mail-line text-2xl text-primary-500 w-7 h-7 flex items-center justify-center mb-2"></i>
                <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                <p className="text-xs text-foreground-500 mt-1">{siteInfo.email}</p>
              </div>
              <div className="flex flex-col items-center p-5 rounded-lg bg-background-100 text-center">
                <i className="ri-phone-line text-2xl text-primary-500 w-7 h-7 flex items-center justify-center mb-2"></i>
                <p className="text-sm font-medium text-foreground-900">客服电话</p>
                <p className="text-xs text-foreground-500 mt-1">{siteInfo.phone}</p>
              </div>
              <div className="flex flex-col items-center p-5 rounded-lg bg-background-100 text-center">
                <i className="ri-map-pin-line text-2xl text-primary-500 w-7 h-7 flex items-center justify-center mb-2"></i>
                <p className="text-sm font-medium text-foreground-900">公司地址</p>
                <p className="text-xs text-foreground-500 mt-1">{siteInfo.address}</p>
              </div>
            </div>

            {formStatus === 'success' ? (
              <div className="text-center p-8 rounded-lg bg-accent-100">
                <i className="ri-check-line text-3xl text-accent-500 w-8 h-8 flex items-center justify-center mx-auto mb-3"></i>
                <p className="font-heading text-lg font-bold text-foreground-900">提交成功！</p>
                <p className="text-sm text-foreground-500 mt-1">感谢您的留言，我们会尽快回复您。</p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-4"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入主题"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  />
                </div>

                {formStatus === 'error' && (
                  <p className="text-sm text-red-500">{formError}</p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                      提交中...
                    </span>
                  ) : (
                    '提交留言'
                  )}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}